const ENDED_KEY = 'examfolio.sessionEnded';

/** How the previous session in this tab ended. */
export interface EndedSession {
  userId: string;
  /** 'logout' = the person chose to leave; 'expired' = the session was cut off mid-work. */
  reason: 'logout' | 'expired';
}

/**
 * Remembers whose session just ended and why, so the next login can decide whether the page it
 * was bounced from is still the right destination. Per tab (sessionStorage), because the pending
 * returnUrl lives in that tab's history state. Storage can be unavailable (private mode,
 * restricted env) — then every returnUrl is honoured, and RoleGate still guards access.
 */
export const endedSession = {
  remember(session: EndedSession): void {
    try {
      sessionStorage.setItem(ENDED_KEY, JSON.stringify(session));
    } catch {
      // best effort
    }
  },
  take(): EndedSession | null {
    try {
      const raw = sessionStorage.getItem(ENDED_KEY);
      sessionStorage.removeItem(ENDED_KEY);
      return raw ? (JSON.parse(raw) as EndedSession) : null;
    } catch {
      return null;
    }
  },
};

/**
 * Where to go after login. A fresh login starts at the user's home (HomeRedirect). A returnUrl is
 * only honoured where the login interrupted something:
 * - nobody was signed in before — a bookmark or a shared link;
 * - the same person's session expired mid-work.
 * After an explicit logout, or when someone else signs in on the same machine (a shared exam PC,
 * an admin testing as a candidate), the previous page is not theirs to resume.
 */
export function postLoginPath(
  requested: string | undefined,
  signedInUserId: string,
  previous: EndedSession | null,
): string {
  if (!requested) return '/';
  if (!previous) return requested;
  if (previous.reason === 'expired' && previous.userId === signedInUserId) return requested;
  return '/';
}
